(() => {
  const node = document.getElementById('mwk-assistant-config');
  if (!node) return;
  const config = JSON.parse(node.textContent), api = config.accountApi, t = config.strings;
  if (!api?.clientId) return;
  const flowKey = 'mwk-designer-v20-account-flow';
  const encoder = new TextEncoder(), decoder = new TextDecoder();
  const uuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  const query = `query MyDesignDrafts($namespace: String!, $key: String!) {
    customer { id metafield(namespace: $namespace, key: $key) { value compareDigest } }
  }`;
  const mutation = `mutation SaveMyDesignDrafts($metafields: [MetafieldsSetInput!]!) {
    metafieldsSet(metafields: $metafields) {
      metafields { key compareDigest }
      userErrors { field message code }
    }
  }`;
  let accessToken, expiresAt = 0, endpointsPromise, identity, authError, returned;
  const error = key => new Error(t[key] || t.save_failed);
  function bytes64(bytes) {
    let binary = '';
    for (let start = 0; start < bytes.length; start += 8192) binary += String.fromCharCode(...bytes.subarray(start, start + 8192));
    return btoa(binary);
  }
  const base64url = bytes => bytes64(bytes).replace(/\+/g,'-').replace(/\//g,'_').replace(/=+$/,'');
  const random = () => base64url(crypto.getRandomValues(new Uint8Array(32)));
  const unbase64 = value => Uint8Array.from(atob(value.replace(/-/g,'+').replace(/_/g,'/')), char => char.charCodeAt(0));
  function allowedEndpoint(value) {
    const url = new URL(value);
    if (url.protocol !== 'https:' || url.hostname !== 'shopify.com') throw error('account_connection_failed');
    return url.href;
  }
  async function fetchJSON(url, options = {}) {
    let response;
    try { response = await fetch(url, {...options, cache:'no-store', referrerPolicy:'no-referrer',redirect:'error'}); }
    catch (_) { throw error('account_connection_failed'); }
    if (!response.ok) throw error('account_connection_failed');
    return response.json();
  }
  function endpoints() {
    if (!endpointsPromise) endpointsPromise = Promise.all([
      fetchJSON('/.well-known/openid-configuration'), fetchJSON('/.well-known/customer-account-api')
    ]).then(([auth, account]) => {
      if (auth.issuer !== api.issuer) throw error('account_connection_failed');
      return {
        auth: allowedEndpoint(auth.authorization_endpoint), token: allowedEndpoint(auth.token_endpoint),
        jwks: allowedEndpoint(auth.jwks_uri), issuer: auth.issuer,
        graphql: allowedEndpoint(account.graphql_api).replace(/\/api\/\d{4}-\d{2}\/graphql$/, '/api/2026-07/graphql')
      };
    }).catch(cause => { endpointsPromise = null; throw cause; });
    return endpointsPromise;
  }
  function flow() {
    try { return JSON.parse(sessionStorage.getItem(flowKey)); } catch (_) { return null; }
  }
  function validateIDTokenFromTokenEndpoint(jwt, saved, discovered) {
    const parts = String(jwt || '').split('.');
    if (parts.length !== 3) throw error('account_connection_failed');
    const header = JSON.parse(decoder.decode(unbase64(parts[0])));
    const claims = JSON.parse(decoder.decode(unbase64(parts[1])));
    const now = Date.now()/1000;
    if (header.alg !== 'RS256' || claims.iss !== discovered.issuer || claims.nonce !== saved.nonce ||
        !(Array.isArray(claims.aud) ? claims.aud.length === 1 && claims.aud[0] === api.clientId : claims.aud === api.clientId) ||
        (claims.azp && claims.azp !== api.clientId) ||
        !Number.isFinite(claims.exp) || claims.exp <= now || !Number.isFinite(claims.iat) || claims.iat > now + 60 || !claims.sub) throw error('account_connection_failed');
    // This function accepts ONLY the direct HTTPS token-endpoint response.
    // OIDC Core 3.1.3.7(6) permits TLS issuer validation for this code flow.
    // Shopify's public JWKS endpoint has no CORS allowance for browsers.
    // State, PKCE, issuer, audience, nonce and expiry remain mandatory.
  }
  const callback = new URL(location.href);
  const isCallback = callback.searchParams.get('mwk-account-callback') === '1';
  const code = isCallback ? callback.searchParams.get('code') : null;
  const callbackState = isCallback ? callback.searchParams.get('state') : null;
  const callbackError = isCallback ? callback.searchParams.get('error') : null;
  if (isCallback) {
    ['code','state','error','error_description','mwk-account-callback'].forEach(key => callback.searchParams.delete(key));
    history.replaceState(history.state,'',callback.pathname+callback.search);
  }
  const ready = (async () => {
    if (!isCallback) return null;
    const saved = flow();
    if (!saved || saved.state !== callbackState) throw error('account_login_expired');
    returned = {draft:saved.draft, name:saved.name || '', intent:saved.intent || 'save'};
    sessionStorage.removeItem(flowKey);
    if (!Number.isFinite(saved.createdAt) || Date.now()-saved.createdAt > 15*60*1000 || saved.createdAt > Date.now()+60000) throw error('account_login_expired');
    if (callbackError || !code) throw error('account_login_cancelled');
    const discovered = await endpoints();
    const token = await fetchJSON(discovered.token, {
      method:'POST', headers:{'Content-Type':'application/x-www-form-urlencoded'},
      body:new URLSearchParams({grant_type:'authorization_code',client_id:api.clientId,redirect_uri:api.redirectUri,code,code_verifier:saved.verifier})
    });
    validateIDTokenFromTokenEndpoint(token.id_token,saved,discovered);
    if (!token.access_token || !Number.isFinite(Number(token.expires_in))) throw error('account_connection_failed');
    accessToken = token.access_token;
    expiresAt = Date.now()+Number(token.expires_in)*1000;
    // Tokens stay in memory. No Admin credential or refresh token is persisted.
    await read();
    return returned;
  })().catch(cause => { authError = cause; accessToken = null; return returned || null; });
  function connected() { return Boolean(accessToken && expiresAt > Date.now()+30000); }
  async function authenticate(draft, name = '', intent = 'save') {
    const discovered = await endpoints(), verifier = random(), state = random(), nonce = random();
    const challenge = base64url(new Uint8Array(await crypto.subtle.digest('SHA-256',encoder.encode(verifier))));
    const saved = {state,nonce,verifier,createdAt:Date.now(),draft,name,intent};
    try { sessionStorage.setItem(flowKey,JSON.stringify(saved)); } catch (_) { throw error('save_failed'); }
    const url = new URL(discovered.auth);
    for (const [key,value] of Object.entries({client_id:api.clientId,response_type:'code',redirect_uri:api.redirectUri,scope:'openid email customer-account-api:full',state,nonce,code_challenge:challenge,code_challenge_method:'S256',locale:document.documentElement.lang || 'de'})) url.searchParams.set(key,value);
    location.assign(url.href);
  }
  async function graphql(operation, variables) {
    if (!connected()) { accessToken = null; throw error('account_login_expired'); }
    const discovered = await endpoints();
    let response;
    try { response = await fetch(discovered.graphql, {
      method:'POST',cache:'no-store',referrerPolicy:'no-referrer',
      headers:{'Content-Type':'application/json',Authorization:accessToken},
      body:JSON.stringify({query:operation,variables}),redirect:'error'
    }); } catch (_) { throw error('account_connection_failed'); }
    if (response.status === 401) { accessToken = null; throw error('account_login_expired'); }
    if (!response.ok) throw error('account_connection_failed');
    const data = await response.json();
    if (data.errors?.length) throw error('account_connection_failed');
    return data.data;
  }
  async function read() {
    const result = await graphql(query,{namespace:api.namespace,key:api.key});
    const customer = result?.customer;
    if (!customer?.id) throw error('account_connection_failed');
    identity = customer.id;
    const field = customer.metafield;
    let store = {version:1,designs:[]};
    if (field) {
      try { store = JSON.parse(field.value); } catch (_) { throw error('account_data_invalid'); }
      if (store?.version !== 1 || !Array.isArray(store.designs)) throw error('account_data_invalid');
    }
    return {store,customerId:identity,digest:field?.compareDigest ?? null};
  }
  function validDraft(draft) {
    return draft?.version === 1 && draft.productUrl === config.productUrl && Array.isArray(draft.product) && draft.product.length;
  }
  async function encodeDraft(draft) {
    if (!validDraft(draft)) throw error('account_data_invalid');
    const data = encoder.encode(JSON.stringify(draft));
    if (data.length > 8*1024*1024) throw error('account_design_too_large');
    if (typeof CompressionStream === 'undefined') return {encoding:'json',data:JSON.stringify(draft)};
    const compressed = await new Response(new Blob([data]).stream().pipeThrough(new CompressionStream('gzip'))).arrayBuffer();
    return {encoding:'gzip-base64',data:bytes64(new Uint8Array(compressed))};
  }
  async function decodeDraft(entry) {
    let json;
    if (entry.encoding === 'gzip-base64' && typeof DecompressionStream !== 'undefined') {
      const stream = new Blob([unbase64(entry.data)]).stream().pipeThrough(new DecompressionStream('gzip'));
      const reader = stream.getReader(), chunks = []; let size = 0;
      while (true) {
        const {value,done} = await reader.read(); if (done) break;
        size += value.length;
        if (size > 8*1024*1024) { await reader.cancel(); throw error('account_design_too_large'); }
        chunks.push(value);
      }
      json = await new Blob(chunks).text();
    } else if (entry.encoding === 'json') json = entry.data;
    else throw error('account_data_invalid');
    const draft = JSON.parse(json);
    if (!validDraft(draft)) throw error('account_data_invalid');
    return draft;
  }
  async function save(draft, name) {
    name = String(name || '').trim();
    if (!name || name.length > 80) throw error('account_name_required');
    const entry = {id:crypto.randomUUID(),name,updatedAt:new Date().toISOString(),...await encodeDraft(draft)};
    for (let attempt = 0; attempt < 3; attempt++) {
      const current = await read();
      const store = {...current.store,designs:[entry,...current.store.designs]};
      const value = JSON.stringify(store);
      if (encoder.encode(value).length > 128*1024) throw error('account_storage_full');
      const result = await graphql(mutation,{metafields:[{ownerId:current.customerId,namespace:api.namespace,key:api.key,type:'json',value,compareDigest:current.digest}]});
      const errors = result?.metafieldsSet?.userErrors;
      if (!Array.isArray(errors)) throw error('account_connection_failed');
      if (!errors.length) return {id:entry.id,name};
      if (errors.every(item => item.code === 'INVALID_COMPARE_DIGEST' || item.code === 'STALE_OBJECT')) continue;
      throw error('save_failed');
    }
    throw error('account_save_conflict');
  }
  async function list() {
    const {store} = await read();
    return store.designs.filter(entry => uuid.test(entry.id) && typeof entry.name === 'string').map(({id,name,updatedAt})=>({id,name,updatedAt}));
  }
  async function load(id) {
    if (!uuid.test(id)) throw error('account_data_invalid');
    const {store} = await read(), entry = store.designs.find(entry => entry.id === id);
    if (!entry) throw error('account_design_missing');
    return {draft:await decodeDraft(entry),name:entry.name};
  }
  window.MWKDesignAccount = Object.freeze({ready,connected,authenticate,save,list,load,error:()=>authError});
})();
